import React, { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard,
  PlusCircle,
  Receipt,
  Car,
  Sparkles,
  History,
  LogOut,
  Menu,
  X,
  Droplets,
  Users,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

const NAV = [
  { to: "/", label: "Panel", icon: LayoutDashboard, end: true, id: "dashboard", perm: "can_view_reports" },
  { to: "/venta", label: "Nueva venta", icon: PlusCircle, id: "new-sale", perm: "can_create_sales" },
  { to: "/gasto", label: "Nuevo gasto", icon: Receipt, id: "new-expense", perm: "can_manage_expenses" },
  { to: "/autos", label: "Tipos de auto", icon: Car, id: "car-types", perm: "can_manage_car_types" },
  { to: "/extras", label: "Extras", icon: Sparkles, id: "extras", perm: "can_manage_extras" },
  { to: "/historial", label: "Historial", icon: History, id: "history", perm: "can_view_history" },
  { to: "/usuarios", label: "Usuarios", icon: Users, id: "users", perm: "can_manage_users" },
];

const ROLE_LABEL = { admin: "Administrador", gerente: "Gerente", caja: "Caja" };

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const onLogout = async () => {
    await logout();
    navigate("/login");
  };

  const allowed = (item) =>
    !item.perm || user?.role === "admin" || user?.permissions?.[item.perm];
  const visibleNav = NAV.filter(allowed);

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      {/* Mobile top bar */}
      <div className="lg:hidden flex items-center justify-between border-b border-slate-200 bg-white px-4 h-14 sticky top-0 z-30">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-md bg-blue-600 text-white flex items-center justify-center">
            <Droplets className="w-4 h-4" />
          </div>
          <span className="font-display font-bold">AquaLav</span>
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setOpen((o) => !o)}
          data-testid="mobile-menu-toggle"
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </Button>
      </div>

      <div className="flex">
        {/* Sidebar */}
        <aside
          className={`${
            open ? "block" : "hidden"
          } lg:block fixed lg:sticky top-14 lg:top-0 left-0 z-20 w-64 h-[calc(100vh-3.5rem)] lg:h-screen bg-white border-r border-slate-200 p-4 lg:p-5 flex flex-col`}
        >
          <div className="hidden lg:flex items-center gap-2 mb-8">
            <div className="w-9 h-9 rounded-lg bg-blue-600 text-white flex items-center justify-center">
              <Droplets className="w-5 h-5" />
            </div>
            <div>
              <div className="font-display font-bold tracking-tight leading-none">
                AquaLav
              </div>
              <div className="text-[11px] text-slate-500 mt-0.5">
                Car wash manager
              </div>
            </div>
          </div>

          <nav className="flex flex-col gap-1">
            {visibleNav.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                onClick={() => setOpen(false)}
                data-testid={`nav-${item.id}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                    isActive
                      ? "bg-blue-50 text-blue-700 border border-blue-100"
                      : "text-slate-600 hover:bg-slate-50 hover:text-slate-900 border border-transparent"
                  }`
                }
              >
                <item.icon className="w-4 h-4" />
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="mt-auto pt-6 border-t border-slate-200">
            <div className="text-xs text-slate-500 mb-2">Sesión</div>
            <div className="text-sm font-medium truncate" data-testid="user-email">
              {user?.email}
            </div>
            {user?.role && (
              <Badge variant="outline" className="mt-2" data-testid="user-role-badge">
                {ROLE_LABEL[user.role] || user.role}
              </Badge>
            )}
            <Button
              variant="outline"
              className="w-full mt-3 justify-start gap-2"
              onClick={onLogout}
              data-testid="logout-button"
            >
              <LogOut className="w-4 h-4" />
              Cerrar sesión
            </Button>
          </div>
        </aside>

        {/* Main */}
        <main className="flex-1 min-w-0 p-4 md:p-8 lg:p-10">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
